import { observer } from 'mobx-react';
import { useStores } from 'stores';
import moment from 'moment';
import cn from 'classnames';
import styles from './LotDetails.module.scss';
import { lotTypesEnum } from 'constants/enums';
import { LocationOnOutlined } from 'uiKit/atoms/LocationOnOutlined/LocationOnOutlined';
import { BNS3_SERVICE_PRODUCT_TYPE } from '../../../../constants';
import { ReactComponent as InfoSvg } from '../../../../assets/img/info.svg';
import { getLotCategoryData, getLotLocationTitle, formatConvertedPrice } from '../../../../helpers/utils';

const LotDetails = () => {
  const { singleLotStore } = useStores();

  const lotAddedDate = moment(singleLotStore.singleLot.added).format('LL');

  const { lotCategories, singleLot, isDraft, inactiveLot } = singleLotStore;

  const {
    productType,
    title,
    fixedPrice,
    currency,
    contact,
    categoryId,
    lotType,
    lotState,
    currencyOrigin,
    fixedPriceOrigin,
  } = singleLot;

  // TEMPORARY WHILE WE HAVE PAGE WITHOUT MAP we will use user location

  const { category, subcategory } = getLotCategoryData(lotCategories, categoryId);

  const lotTypeClassName = lotType === 'Offer' ? styles.bns3_lot_type_offer : styles.bns3_lot_type_lookingfor;
  const lotPriceClassName = fixedPrice > 0 ? styles.bns3_lot_price_fixed : styles.bns3_free_lot;

  const renderInactiveLotStatus = () => (
    <div className={styles.bns3_lot_status_container}>
      <InfoSvg className={styles.bns3_lot_status_icon} />
      <p className={styles.bns3_lot_status_text}>
        {isDraft ? "It's a draft version of the lot." : 'The lot was deactivated.'}
      </p>
    </div>
  );

  return (
    <>
      <h2 className={styles.bns3_visually_hidden}>Lot Details</h2>

      <div data-testid="contact-info-contact">
        <div className={styles.bns3_lot_info_container}>
          {inactiveLot && renderInactiveLotStatus()}
          <p className={styles.bns3_lot_date}>{lotAddedDate}</p>
          <p className={styles.bns3_lot_data_container}>
            <span className={cn(styles.bns3_lot_type, lotTypeClassName)}>{lotTypesEnum[lotType]}</span>
            <span className={styles.bns3_product_type}>{productType}</span>
            {productType !== BNS3_SERVICE_PRODUCT_TYPE && <span className={styles.bns3_lot_state}>{lotState}</span>}
          </p>
          <h3 className={styles.bns3_lot_title}>{title}</h3>
          <div className={styles.bns3_lot_location_container}>
            <LocationOnOutlined className={styles.bns3_lot_location_icon} />
            <p className={styles.bns3_lot_location_text}>{getLotLocationTitle(singleLot)}</p>
          </div>
          <span className={cn(styles.bns3_lot_price, lotPriceClassName)}>
            {fixedPrice > 0 ? `${currency} ${formatConvertedPrice(fixedPrice)}` : 'For Free'}
          </span>
          {fixedPriceOrigin > 0 && currencyOrigin && (
            <span className={cn(styles.bns3_lot_origin_price, lotPriceClassName)}>
              {`${currencyOrigin} ${formatConvertedPrice(fixedPriceOrigin)}`}
            </span>
          )}
          {category && subcategory && (
            <p className={styles.bns3_lot_category}>{`${category.name}, ${subcategory.name}`}</p>
          )}
          {category && !subcategory && <p className={styles.bns3_lot_category}> {category.name} </p>}
        </div>

        <div className={styles.bns3_contact_person_container}>
          <h3 className={styles.bns3_contact_person_title}>Contact Person</h3>

          <p className={styles.bns3_contact_person_data_title}>
            Name
            <span className={styles.bns3_contact_person_data}>
              {contact ? (
                `${contact.firstName} ${contact.lastName}`
              ) : (
                <span className={styles.bns3_contact_data_not_provided}>Name is not provided</span>
              )}
            </span>
          </p>
          <p className={styles.bns3_contact_person_data_title}>
            Phone
            <span className={styles.bns3_contact_person_data}>
              {contact && contact.phone ? (
                <a href={`tel:${contact.phone}`} data-testid="contact-info-phone">
                  {contact.phone}
                </a>
              ) : (
                <span className={styles.bns3_contact_data_not_provided}>Phone is not provided</span>
              )}
            </span>
          </p>
          <p className={styles.bns3_contact_person_data_title}>
            Email
            <span className={styles.bns3_contact_person_data}>
              {contact && contact.email ? (
                <a href={`mailto:${contact.email}`} data-testid="contact-info-email">
                  {contact.email}
                </a>
              ) : (
                <span className={styles.bns3_contact_data_not_provided}>Email is not provided</span>
              )}
            </span>
          </p>
        </div>
      </div>
    </>
  );
};

export default observer(LotDetails);
